/**
 * A very small STOMP client over the browser's own WebSocket.
 *
 * The dashboard needs exactly one thing from a message broker: to be told that a branch's day-board
 * has moved (FR-401, ≤ 3 s). STOMP is a line-oriented text protocol and that use of it is about
 * eighty lines, which is a better trade than a dependency — and a smaller supply-chain surface on
 * an application that handles patient data.
 *
 * What it does: connect, authenticate on the CONNECT frame, subscribe, hand MESSAGE bodies to a
 * callback, keep the connection alive with heartbeats, and reconnect with backoff. What it
 * deliberately does not do: transactions, acknowledgements, receipts, or SockJS fallback — none of
 * which a fire-and-forget notification needs.
 */

/** STOMP terminates every frame with a NULL octet. */
const NULL = '\u0000';
const HEARTBEAT_MS = 10_000;
const FIRST_RETRY_MS = 1_000;
const MAX_RETRY_MS = 30_000;

export interface StompSubscription {
  readonly destination: string;
  readonly onMessage: (body: unknown) => void;
}

export interface StompClientOptions {
  readonly url: string;
  /** Read on every (re)connect, so a refreshed access token is picked up without reconnecting twice. */
  readonly accessToken: () => string | null;
  readonly subscriptions: readonly StompSubscription[];
  readonly onStateChange?: (connected: boolean) => void;
}

interface Frame {
  readonly command: string;
  readonly headers: Record<string, string>;
  readonly body: string;
}

function encode(command: string, headers: Record<string, string>, body = ''): string {
  const lines = Object.entries(headers).map(([key, value]) => `${key}:${value}`);
  return `${command}\n${lines.join('\n')}\n\n${body}${NULL}`;
}

function decode(raw: string): Frame | null {
  const payload = raw.endsWith(NULL) ? raw.slice(0, -1) : raw;
  const separator = payload.indexOf('\n\n');
  if (separator < 0) {
    // A bare newline is the server's heartbeat. Nothing to do: receiving it is the point.
    return null;
  }
  const [command, ...headerLines] = payload.slice(0, separator).split('\n');
  const headers: Record<string, string> = {};
  headerLines.forEach((line) => {
    const colon = line.indexOf(':');
    if (colon > 0) {
      headers[line.slice(0, colon)] = line.slice(colon + 1);
    }
  });
  return { command: command ?? '', headers, body: payload.slice(separator + 2) };
}

/**
 * Opens the connection and keeps it open until the returned function is called.
 *
 * Returns a disposer rather than an object with methods because that is the whole lifecycle a
 * React effect needs, and an object invites somebody to hold on to it.
 */
export function connectStomp(options: StompClientOptions): () => void {
  let socket: WebSocket | null = null;
  let heartbeat: ReturnType<typeof setInterval> | null = null;
  let retry: ReturnType<typeof setTimeout> | null = null;
  let attempt = 0;
  let closed = false;

  const stopHeartbeat = () => {
    if (heartbeat !== null) {
      clearInterval(heartbeat);
      heartbeat = null;
    }
  };

  const scheduleReconnect = () => {
    if (closed || retry !== null) {
      return;
    }
    // Exponential with a ceiling: a server that is down for ten minutes should not be hammered,
    // and a board that has been dark for thirty seconds should not take another five to notice it
    // is back.
    const delay = Math.min(FIRST_RETRY_MS * 2 ** attempt, MAX_RETRY_MS);
    attempt += 1;
    retry = setTimeout(() => {
      retry = null;
      open();
    }, delay);
  };

  function open() {
    const token = options.accessToken();
    if (closed || !token) {
      return;
    }

    socket = new WebSocket(options.url);

    socket.onopen = () => {
      socket?.send(
        encode('CONNECT', {
          'accept-version': '1.2',
          host: window.location.host,
          'heart-beat': `${HEARTBEAT_MS},${HEARTBEAT_MS}`,
          Authorization: `Bearer ${token}`,
        }),
      );
    };

    socket.onmessage = (event: MessageEvent<string>) => {
      const frame = decode(event.data);
      if (frame === null) {
        return;
      }

      if (frame.command === 'CONNECTED') {
        attempt = 0;
        options.onStateChange?.(true);
        options.subscriptions.forEach((subscription, index) => {
          socket?.send(
            encode('SUBSCRIBE', {
              id: `sub-${index}`,
              destination: subscription.destination,
            }),
          );
        });
        stopHeartbeat();
        heartbeat = setInterval(() => socket?.send('\n'), HEARTBEAT_MS);
        return;
      }

      if (frame.command === 'MESSAGE') {
        const destination = frame.headers['destination'];
        options.subscriptions
          .filter((subscription) => subscription.destination === destination)
          .forEach((subscription) => {
            try {
              subscription.onMessage(frame.body ? JSON.parse(frame.body) : null);
            } catch {
              // A body we cannot read is not a reason to tear down the connection; the next
              // message will be fine, and the board's periodic refetch covers the gap.
            }
          });
        return;
      }

      if (frame.command === 'ERROR') {
        // The server refused us — an expired token, or a subscription to a clinic this account is
        // not in. Closing triggers the backoff, which will re-read the token on the way back.
        socket?.close();
      }
    };

    socket.onclose = () => {
      stopHeartbeat();
      options.onStateChange?.(false);
      socket = null;
      scheduleReconnect();
    };

    socket.onerror = () => socket?.close();
  }

  open();

  return () => {
    closed = true;
    stopHeartbeat();
    if (retry !== null) {
      clearTimeout(retry);
    }
    if (socket !== null && socket.readyState === WebSocket.OPEN) {
      socket.send(encode('DISCONNECT', {}));
    }
    socket?.close();
    socket = null;
  };
}

/** Turns the API base URL into the socket URL, keeping http↔ws and https↔wss in step. */
export function socketUrlFrom(apiBaseUrl: string): string {
  const base = new URL(apiBaseUrl, window.location.origin);
  const protocol = base.protocol === 'https:' ? 'wss:' : 'ws:';
  // The API lives under /api/v1; the socket is a sibling of it, not a child.
  const path = base.pathname.replace(/\/api\/v\d+\/?$/, '');
  return `${protocol}//${base.host}${path}/ws`;
}
